import { useEffect, useRef, useState } from 'react'
import { PageHeader } from '../../../components/PageHeader'
import { WeeklySubmittedScreen } from '../../../components/WeeklySubmittedScreen'
import { supabase } from '../../../lib/supabase'
import { readError } from '../../../lib/errors'
import {
  buildPromptText, ensureMyThreeStepWeeklyEntry, fetchMyThreeStepWeeklyEntry,
  saveThreeStepWeeklyEntry, withdrawThreeStepWeeklyEntry,
  type ThreeStepWeeklyEntryRow, type ThreeStepWeeklyEventRow,
  weekRangeLabel,
} from '../api/weekly'
import { useAuth } from '../../../lib/auth'
import { useT } from '../../../i18n'

const MAX_STEP = 160

interface Props {
  eventId: string
  entryId: string
  onBack: () => void
}

/**
 * The "write your three steps" surface. Three text fields, each
 * capped at 160 characters, the prompt rendered above them.
 *
 * Auto-saves on blur of each field. The final "Save & Done" button
 * forces a save and routes back to the hub.
 *
 * If the event has been finalized between page-load and now (the
 * Monday rollover happened while the screen was open), the save RPC
 * surfaces a "submissions are locked" error and we keep the user's
 * draft text on screen so they don't lose what they typed.
 */
export function ThreeStepWeeklyPlayScreen({ eventId, entryId, onBack }: Props) {
  const t = useT()
  const { profile } = useAuth()
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)
  const discardTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [event, setEvent] = useState<ThreeStepWeeklyEventRow | null>(null)
  const [entry, setEntry] = useState<ThreeStepWeeklyEntryRow | null>(null)
  const [s1, setS1] = useState('')
  const [s2, setS2] = useState('')
  const [s3, setS3] = useState('')
  const [loading, setLoading] = useState(true)
  const [savingState, setSavingState] = useState<'idle' | 'saving' | 'saved'>('idle')
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ev = await (supabase as any)
          .from('three_step_weekly_events')
          .select('*')
          .eq('id', eventId)
          .single()
        if (ev.error) throw ev.error
        if (cancelled) return
        setEvent(ev.data as ThreeStepWeeklyEventRow)

        // Read-only on open: no row exists until something is typed.
        // The row is created inside save() on the first non-empty
        // blur, so opening this screen never mints an entry.
        const mine = profile ? await fetchMyThreeStepWeeklyEntry(eventId, profile.id) : null
        if (cancelled) return
        setEntry(mine)
        setS1(mine?.step1_text ?? '')
        setS2(mine?.step2_text ?? '')
        setS3(mine?.step3_text ?? '')
      } catch (e) {
        if (!cancelled) setError(readError(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [eventId, entryId])

  useEffect(() => () => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
  }, [])

  async function save(n1: string, n2: string, n3: string): Promise<boolean> {
    let row = entry
    if (!row) {
      // Nothing typed yet: quietly do nothing rather than create an
      // empty row for someone who tapped in and out of a field.
      if (![n1, n2, n3].some(v => v.trim().length > 0)) return true
      try {
        row = await ensureMyThreeStepWeeklyEntry(eventId)
        setEntry(row)
      } catch (e) {
        setError(readError(e))
        return false
      }
    }
    if (row.is_final) {
      setError(t("This week is locked for voting. You can't edit it anymore."))
      return false
    }
    setSavingState('saving'); setError(null)
    try {
      const saved = await saveThreeStepWeeklyEntry(row.id, n1, n2, n3)
      setEntry(saved)
      setSavingState('saved')
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
      savedTimerRef.current = setTimeout(() => setSavingState('idle'), 1500)
      return true
    } catch (e) {
      setSavingState('idle')
      setError(readError(e))
      return false
    }
  }

  if (loading) {
    return (
      <div className="screen">
        <PageHeader title={t('Your three steps')} onBack={onBack} />
        <div className="flex-1 px-5 py-5 space-y-3">
          <div className="h-24 rounded-2xl bg-brand-elevated animate-pulse" />
          <div className="h-12 rounded-2xl bg-brand-elevated animate-pulse" />
          <div className="h-12 rounded-2xl bg-brand-elevated animate-pulse" />
          <div className="h-12 rounded-2xl bg-brand-elevated animate-pulse" />
        </div>
      </div>
    )
  }

  if (!event) {
    return (
      <div className="screen">
        <PageHeader title={t('Your three steps')} onBack={onBack} />
        <div className="flex-1 flex items-center justify-center px-5 text-center">
          <p className="text-sm text-brand-danger">{error ?? t('Could not load your entry.')}</p>
        </div>
      </div>
    )
  }

  const filled =
    [s1, s2, s3].filter(s => s.trim().length > 0).length
  const ready = filled === 3
  const locked = entry?.is_final ?? false
  const previouslySubmitted = entry?.is_final ?? false

  if (submitted) {
    return (
      <WeeklySubmittedScreen
        title={t('Weekly · Three Step')}
        accent="#0EAE93"
        message={t("Your solution is entered in this week's event. Winners are revealed next Monday. Good luck!")}
        onBackToMenu={onBack}
        onReview={locked ? undefined : () => setSubmitted(false)}
        reviewLabel={t('Review / edit my solution')}
      />
    )
  }

  return (
    <div className="screen">
      <PageHeader
        title={t('Your three steps')}
        subtitle={`${weekRangeLabel(event.week_start)} · ${locked ? t('Locked, voting is open') : t('auto-saves as you type')}`}
        onBack={onBack}
      />

      <div className="flex-1 flex flex-col px-5 py-5 gap-5 overflow-y-auto pb-36">
        {/* Prompt */}
        <div className="bg-game-three-step-light border-2 border-game-three-step-dark
                        rounded-2xl shadow-card p-4">
          <p className="text-xs font-semibold text-game-three-step-dark
                        uppercase tracking-wide mb-2">
            {t("This week's prompt")}
          </p>
          <p className="font-display text-lg font-bold text-brand-text leading-snug">
            {buildPromptText(event.action_a, event.action_b, t)}
          </p>
        </div>

        {previouslySubmitted && !locked && (
          <div className="rounded-2xl border-2 border-brand-danger/40 bg-brand-danger/5 p-4">
            <p className="font-semibold text-brand-danger text-sm">
              {t('Editing resets your votes')}
            </p>
            <p className="text-xs text-brand-text mt-1 leading-relaxed">{t('You already submitted this week. If you save it again you will lose any votes it has already received and the new version will start from zero.')}</p>
          </div>
        )}

        <StepField
          label={t('Step 1')}
          value={s1}
          onChange={setS1}
          onBlur={() => save(s1, s2, s3)}
          disabled={locked}
          placeholder={t("What's the first move?")}
        />
        <StepField
          label={t('Step 2')}
          value={s2}
          onChange={setS2}
          onBlur={() => save(s1, s2, s3)}
          disabled={locked}
          placeholder={t('Then what?')}
        />
        <StepField
          label={t('Step 3')}
          value={s3}
          onChange={setS3}
          onBlur={() => save(s1, s2, s3)}
          disabled={locked}
          placeholder={t('Stick the landing.')}
        />

        <p className="text-xs text-brand-muted h-4">
          {error
            ? <span className="text-brand-danger">{error}</span>
            : savingState === 'saving' ? t('Saving…')
            : savingState === 'saved'  ? t('Saved')
            : ''}
        </p>

        {entry && !locked && (
          <button
            type="button"
            onClick={async () => {
              if (!confirmingDiscard) {
                setConfirmingDiscard(true)
                if (discardTimerRef.current) clearTimeout(discardTimerRef.current)
                discardTimerRef.current = setTimeout(() => setConfirmingDiscard(false), 3500)
                return
              }
              try {
                await withdrawThreeStepWeeklyEntry(entry.id)
                onBack()
              } catch (e) { setError(readError(e)) }
            }}
            className={`w-full py-3 text-sm rounded-2xl border transition-colors
                        ${confirmingDiscard
                          ? 'border-brand-danger text-white bg-brand-danger font-semibold'
                          : 'border-brand-border text-brand-muted bg-white'}`}
          >
            {confirmingDiscard ? t('Tap again to throw it out') : t('Throw out my entry')}
          </button>
        )}

        <div className="card p-4 bg-brand-elevated">
          <p className="text-xs font-semibold text-brand-text mb-1">{t('Remember')}</p>
          <ul className="text-xs text-brand-muted space-y-1">
            <li>{t('• Edit any time this week. Saves as you type.')}</li>
            <li>{t('• Next week, voting opens on every entry.')}</li>
            <li>{t('• Most votes wins and joins the archive.')}</li>
          </ul>
        </div>
      </div>

      <div className="floating-actions">
        <button
          onClick={async () => { if (await save(s1, s2, s3)) setSubmitted(true) }}
          disabled={!ready || locked}
          className="btn-three-step
                     disabled:opacity-40"
        >
          {locked
            ? t('Locked. Go vote')
            : filled === 0
              ? t('Write step 1 to begin')
              : filled < 3
                ? t('Write the rest ({n}/3)', { n: filled })
                : t('Save & Done')}
        </button>
      </div>
    </div>
  )
}

interface StepFieldProps {
  label: string
  value: string
  onChange: (next: string) => void
  onBlur: () => void
  disabled: boolean
  placeholder: string
}

function StepField({
  label, value, onChange, onBlur, disabled, placeholder,
}: StepFieldProps) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-brand-muted uppercase tracking-wide">
          {label}
        </p>
        <span className={`text-xs font-medium
          ${value.length > MAX_STEP * 0.85 ? 'text-brand-danger' : 'text-brand-muted'}`}>
          {value.length}/{MAX_STEP}
        </span>
      </div>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value.slice(0, MAX_STEP))}
        onBlur={onBlur}
        rows={2}
        disabled={disabled}
        placeholder={placeholder}
        className="w-full px-4 py-3 rounded-2xl border border-brand-border bg-white
                   text-brand-text placeholder:text-brand-muted/60 resize-none text-sm
                   focus:outline-hidden focus:border-game-three-step-dark focus:ring-2
                   focus:ring-game-three-step-dark/20 transition-all leading-relaxed
                   disabled:opacity-70 disabled:cursor-not-allowed"
      />
    </div>
  )
}
